import { useSnackbar } from "@homebound/beam";
import { useNavigate } from "react-router-dom";
import { WorkflowActionArgs } from "src/routes/projects/change-events/guided/workflows/types";
import { createChangeEventsUrl } from "src/RouteUrls";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";
import {
  useApplyConfigurationDirectly_ReadyPlanConfigQuery,
  useApplyConfigurationDirectlyMutation,
} from "./useApplyConfigurationDirectly.generated";

/**
 * Applies the selected configuration straight onto the project, for a stage with no signed contract.
 *
 * The backend's `ProjectReadyPlanConfig.apply` makes the same call: with no signed contract it mutates the project's
 * items in place instead of routing a change event, so there is nothing to submit for approval afterwards.
 */
export function useApplyConfigurationDirectly(args: WorkflowActionArgs<ApplyConfigurationFormInput>) {
  const { form, projectId } = args;
  const navigate = useNavigate();
  const { triggerNotice } = useSnackbar();
  const [applyConfiguration] = useApplyConfigurationDirectlyMutation();
  // The gate already ran this, so it comes back from cache.
  const { data } = useApplyConfigurationDirectly_ReadyPlanConfigQuery({ variables: { projectId } });
  const readyPlanConfigId = data?.structure.readyPlanConfig?.id;

  return async () => {
    if (!form.canSave()) return;

    await applyConfiguration({
      variables: {
        input: {
          projectReadyPlanConfigs: [
            {
              // A project that has never been configured has nothing to update, so create its config instead.
              ...(readyPlanConfigId ? { id: readyPlanConfigId } : { projectId }),
              readyPlanId: form.readyPlanId.value,
              options: form.proposedReadyPlanOptions.value.map(({ readyPlanOptionId }) => ({ readyPlanOptionId })),
              // `orientation` is deliberately omitted: the guided form never collects it, and leaving it out
              // keeps whatever the project already has.
            },
          ],
          overwriteProgramDataWithComputed: true,
        },
      },
    });

    // Nothing was ever saved as a change event, so drop the form's changes to keep the unsaved-changes guard quiet.
    form.revertChanges();
    triggerNotice({ message: "Configuration applied" });
    navigate(createChangeEventsUrl(projectId));
  };
}
